import { OrgAccessRefMapper } from '@csp/csp-common-mapper';
import { Maybe, OrgAccessRef, OrgId, OrgIdsByRole, User } from '@csp/csp-common-model';
import { isRoleType } from '@csp/dmdp-api-common-dto';
import { RoleAccessRefsV1, UserV1 } from '@csp/dmdp-api-user-dto';
import { first, uniq } from 'lodash';

const getActiveOrgAccessRefs = (roleAccessRefs: RoleAccessRefsV1[]): OrgAccessRef[] => {
  const allAccessRefs = roleAccessRefs.flatMap(roleAccessRef => roleAccessRef.accessRefs ?? []);
  return OrgAccessRefMapper.fromAccessRefs(allAccessRefs).filter(({ isActive }) => isActive);
};

const getUserOrgs = (
  roleAccessRefs: Maybe<RoleAccessRefsV1[]>,
): {
  activeOrgAccessRefs: OrgAccessRef[];
  firstOrgAccessRef: Maybe<OrgAccessRef>;
  orgIdsIncludingParents: OrgId[];
  orgIds: OrgId[];
} => {
  const activeOrgAccessRefs = getActiveOrgAccessRefs(roleAccessRefs ?? []);
  const parentOrgIds = uniq(activeOrgAccessRefs.flatMap(({ parentOrgIds }) => parentOrgIds));
  const firstOrgAccessRef = first(activeOrgAccessRefs);
  const orgIds = uniq(activeOrgAccessRefs.map(({ orgId }) => orgId));
  const orgIdsIncludingParents = uniq([...parentOrgIds, ...orgIds]);

  return { activeOrgAccessRefs, firstOrgAccessRef, orgIdsIncludingParents, orgIds };
};

const isBelongingToSameOrgs = (user: User, otherUser: User): boolean =>
  otherUser.activeOrgAccessRefs.every(accessRefs => {
    if (user.orgIds.includes(accessRefs.orgId)) {
      return true;
    }
    if (accessRefs.parentOrgIds.some(parentOrgId => user.orgIds.includes(parentOrgId))) {
      return true;
    }

    return false;
  });

// Returns an object with role as the key and corresponding org ids that
// the role applies to for that user. For example if the following object is returned:
// {
//    PI: ["orgId1", "orgId3"],
//    CRA: ["orgId2"]
// }
// It means that the user is a PI in the organizations with ids "orgId1" and "orgId3",
// and also CRA in the organization with id "orgId2".
const getOrgIdsByRole = (userV1: Partial<UserV1>): OrgIdsByRole => {
  const roleAccessRefs = userV1.roleAccessRefs ?? []; // Even though the type is required, roleAccessRefs are not always present due to query API fields concept.
  return roleAccessRefs.reduce<OrgIdsByRole>((orgIdsByRole, { accessRefs, role }) => {
    if (!accessRefs) {
      return orgIdsByRole;
    }
    const orgAccessRefs = OrgAccessRefMapper.fromAccessRefs(accessRefs);

    if (!isRoleType(role)) {
      return orgIdsByRole;
    }

    const orgIds = orgIdsByRole[role] ?? [];
    for (const accessRef of orgAccessRefs) {
      if (!orgIds.includes(accessRef.orgId) && accessRef.isActive) {
        orgIds.push(accessRef.orgId);
      }
    }

    orgIdsByRole[role] = orgIds;
    return orgIdsByRole;
  }, {});
};

export const UserUtil = {
  getUserOrgs,
  getOrgIdsByRole,
  isBelongingToSameOrgs,
};
